"use client";

import { motion } from "framer-motion";
import { useState, type ReactNode } from "react";
import { cn } from "@/lib/cn";
import { EASE, spring } from "@/lib/motion";
import { PlatformError } from "@/lib/api/client";
import { useDebounced, useGameBrowse } from "@/lib/api/hooks";
import { messageFor } from "@/lib/api/messages";
import { useI18n } from "@/lib/i18n/I18nProvider";
import { Icon, type IconName } from "@/components/ui/Icon";
import { SectionEmpty, SectionError, TileSkeleton } from "@/components/ui/states";
import { GameGrid } from "./GameSections";

export type BrowseCategory = { id: string | null; slug: string; name: string; icon: IconName };

export type BrowseStudio = { id: string | null; slug: string; name: string };

export const ALL_GAMES: BrowseCategory = { id: null, slug: "all", name: "All Games", icon: "spade" };

export const ALL_STUDIOS: BrowseStudio = { id: null, slug: "all", name: "All Studios" };

/* `home` is the homepage's browser: the tall pills, then the search field.
   `lobby` is the casino lobby page as the kit draws it: the pill-shaped
   search bar first, the small pills under it. */
export type GameBrowserVariant = "home" | "lobby";

/* Owns the lobby's filter and query. While both are untouched the curated
   sections show; the moment either changes we swap in a live result grid
   fetched from the API — server-side search, not a filter over one page. */
export function GameBrowser({
  sections,
  categories,
  studios = [],
  initialCategorySlug = "all",
  initialStudioSlug = "all",
  variant = "home",
  search = true,
  pills: pagePills,
}: {
  sections?: ReactNode;
  categories: BrowseCategory[];
  studios?: BrowseStudio[];
  initialCategorySlug?: string;
  initialStudioSlug?: string;
  variant?: GameBrowserVariant;
  /* Whether the search field is drawn. The pills alone still browse. */
  search?: boolean;
  /* Pills the page draws in place of the browser's own filters: the
     category page's Category pills — links to each category's page, not
     filters — so every page that browses games shares one row. */
  pills?: ReactNode;
}) {
  const { t } = useI18n();
  const lobby = variant === "lobby";
  const options = [ALL_GAMES, ...categories];
  const initial = options.find((option) => option.slug === initialCategorySlug) ?? ALL_GAMES;

  const studioOptions = [ALL_STUDIOS, ...studios];
  const initialStudio =
    studioOptions.find((option) => option.slug === initialStudioSlug) ?? ALL_STUDIOS;

  const [selected, setSelected] = useState(initial);
  const [studio, setStudio] = useState(initialStudio);
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounced(query.trim());

  const browsing = selected.id !== null || studio.id !== null || debouncedQuery.length > 0;

  const browse = useGameBrowse({
    categoryId: selected.id,
    query: debouncedQuery,
    studioIds: studio.id ? [studio.id] : undefined,
    enabled: browsing,
  });

  const pills = pagePills ?? (
      <div
      className={cn(
        "scrollbar-none flex items-start gap-[8px] overflow-x-auto overflow-y-hidden",
        lobby ? "" : "rounded-2xl bg-elevation-1 p-[8px] lg:bg-transparent lg:p-0",
      )}
    >
        {options.map((option) => (
          <button
            key={option.slug}
            type="button"
            onClick={() => setSelected(option)}
            className={cn(
              "relative flex shrink-0 items-center font-bold leading-[1.6] transition-colors duration-200",
              lobby
                ? "h-[32px] gap-[4px] rounded-rounded px-[16px] text-[12px]"
                : "h-[44px] gap-[4px] rounded-lg px-[12px] text-[14px] lg:h-[40px] lg:gap-[8px] lg:rounded-rounded lg:px-[20px] lg:text-[12px]",
              selected.slug === option.slug
                ? "text-fg-on-primary"
                : lobby
                  ? "bg-elevation-2 text-fg-subtle hover:bg-elevation-3 hover:text-fg"
                  : "text-fg-subtle hover:bg-elevation-2 hover:text-fg lg:bg-elevation-2",
            )}
          >
            {selected.slug === option.slug && (
              <motion.span
                layoutId="filter-pill"
                transition={spring}
                className={cn("absolute inset-0 bg-brand", lobby ? "rounded-rounded" : "rounded-lg lg:rounded-rounded")}
              />
            )}
            <Icon name={option.icon} size={lobby ? 14 : 18} className="relative" />
            <span className="relative">{option.name}</span>
          </button>
        ))}
      </div>
  );

  const searchField = search ? (
      <label
        className={cn(
          "flex items-center gap-[7px] border bg-elevation-1 transition-colors duration-200 focus-within:border-brand",
          lobby ? "h-[40px] rounded-rounded border-transparent px-[20px]" : "h-[50px] rounded-2xl border-border px-[12px] py-[8px]",
        )}
      >
        <Icon name="search" size={lobby ? 16 : 18} className={cn("shrink-0", lobby ? "text-fg-subtle" : "text-fg")} />
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={lobby ? t("lobby.searchAll") : "Search Games"}
          aria-label={t("topbar.searchGames")}
          className={cn(
            "h-full min-w-0 flex-1 bg-transparent font-bold leading-[1.6] text-fg outline-none",
            lobby ? "text-[12px] placeholder:text-fg-subtle" : "text-[14px] placeholder:text-fg",
          )}
        />
        {browse.isFetching && browsing && (
          <span className="h-[14px] w-[14px] shrink-0 animate-spin rounded-full border-2 border-fg-disabled border-t-brand-bold" />
        )}
        {query && (
          <button
            type="button"
            onClick={() => setQuery("")}
            aria-label="Clear search"
            className="shrink-0 text-fg-subtle hover:text-fg"
          >
            <Icon name="close" size={18} />
          </button>
        )}
      </label>
  ) : null;

  return (
    <>
      {lobby ? (
        <>
          {searchField}
          {pills}
        </>
      ) : (
        <>
          {pills}
      {studios.length > 0 && (
        <div className="scrollbar-none flex items-start gap-[8px] overflow-x-auto overflow-y-hidden">
          {studioOptions.map((option) => (
            <button
              key={option.slug}
              type="button"
              onClick={() => setStudio(option)}
              className={cn(
                "relative flex h-[32px] shrink-0 items-center rounded-rounded px-[16px] text-[12px] font-bold leading-[1.6] transition-colors duration-200",
                studio.slug === option.slug ? "text-fg-on-primary" : "bg-elevation-2 text-fg-subtle hover:text-fg",
              )}
            >
              {studio.slug === option.slug && (
                <motion.span
                  layoutId="studio-pill"
                  transition={spring}
                  className="absolute inset-0 rounded-rounded bg-brand"
                />
              )}
              <span className="relative">{option.name}</span>
            </button>
          ))}
        </div>
      )}
          {searchField}
        </>
      )}

      {browsing ? (
        <motion.div
          key="results"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.28, ease: EASE }}
          className="flex flex-col gap-4"
        >
          <div className="flex items-center justify-between">
            <p className="text-[14px] font-bold">
              {browse.isPending ? (
                <span className="text-fg-subtle">Searching…</span>
              ) : (
                <>
                  {browse.total} {browse.total === 1 ? "game" : "games"}
                  {selected.id && <span className="text-fg-subtle"> in {selected.name}</span>}
                  {studio.id && <span className="text-fg-subtle"> from {studio.name}</span>}
                  {debouncedQuery && (
                    <span className="text-fg-subtle"> matching &ldquo;{debouncedQuery}&rdquo;</span>
                  )}
                </>
              )}
            </p>
            <button
              type="button"
              onClick={() => {
                setSelected(ALL_GAMES);
                setStudio(ALL_STUDIOS);
                setQuery("");
              }}
              className="text-[12px] font-bold text-brand-fg-accent hover:underline"
            >
              Reset
            </button>
          </div>

          <Results browse={browse} />
        </motion.div>
      ) : (
        <motion.div
          key="sections"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.28, ease: EASE }}
          className="flex flex-col gap-6"
        >
          {sections}
        </motion.div>
      )}
    </>
  );
}

function Results({ browse }: { browse: ReturnType<typeof useGameBrowse> }) {
  if (browse.isPending) {
    return (
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
        {Array.from({ length: 14 }, (_, index) => (
          <TileSkeleton key={index} />
        ))}
      </div>
    );
  }

  if (browse.isError) {
    const error =
      browse.error instanceof PlatformError
        ? browse.error
        : new PlatformError(0, "network", "Unknown failure.");

    return (
      <div className="rounded-2xl bg-elevation-1">
        <SectionError
          title="Search didn’t work"
          body={messageFor(error, "games")}
          action={
            <button
              type="button"
              onClick={() => browse.refetch()}
              className="h-9 rounded-lg bg-elevation-3 px-4 text-[12px] font-bold transition-colors hover:bg-elevation-4"
            >
              Try again
            </button>
          }
        />
      </div>
    );
  }

  if (browse.games.length === 0) {
    return (
      <div className="rounded-2xl bg-elevation-1">
        <SectionEmpty
          icon="ghost"
          title="No games match that"
          body="Try a different studio or clear the filter to see the full lobby."
        />
      </div>
    );
  }

  return (
    <>
      <GameGrid games={browse.games} />

      {browse.hasNextPage && (
        <button
          type="button"
          onClick={() => browse.fetchNextPage()}
          disabled={browse.isFetchingNextPage}
          className="mx-auto h-10 rounded-lg bg-elevation-1 px-6 text-[12px] font-bold transition-colors hover:bg-elevation-2 disabled:opacity-60"
        >
          {browse.isFetchingNextPage ? "Loading…" : "Load more"}
        </button>
      )}
    </>
  );
}
